/**
 * section-featured-products.js
 * Enables Splide only on tablet (768-1023px) for featured products sections.
 * Desktop (≥1024px) and mobile (≤767px) use the CSS grid — no Splide.
 */

(function () {
  if (window.__featuredProductsSplideScriptLoaded) return;
  window.__featuredProductsSplideScriptLoaded = true;

  const TABLET_QUERY = '(min-width: 768px) and (max-width: 1023px)';

  if (!window.__openFeaturedProductsCartDrawer) {
    window.__openFeaturedProductsCartDrawer = async function (triggerElement) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return false;

      try {
        const [drawerMarkup, bubbleMarkup] = await Promise.all([
          fetch(`${window.routes.cart_url}?section_id=cart-drawer`).then((response) => response.text()),
          fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`)
            .then((response) => response.text())
            .catch(() => null),
        ]);

        const drawerDocument = new DOMParser().parseFromString(drawerMarkup, 'text/html');
        const sourceDrawer = drawerDocument.querySelector('#CartDrawer');
        const targetDrawer = cartDrawer.querySelector('#CartDrawer');

        if (sourceDrawer && targetDrawer) {
          targetDrawer.innerHTML = sourceDrawer.innerHTML;
        }

        if (bubbleMarkup) {
          const bubbleDocument = new DOMParser().parseFromString(bubbleMarkup, 'text/html');
          const sourceBubble = bubbleDocument.getElementById('cart-icon-bubble');
          const targetBubble = document.getElementById('cart-icon-bubble');

          if (sourceBubble && targetBubble) {
            targetBubble.innerHTML = sourceBubble.innerHTML;
          }
        }

        const isEmpty = sourceDrawer?.querySelector('cart-drawer-items')?.classList.contains('is-empty') ?? false;

        if (typeof cartDrawer.setEmptyState === 'function') {
          cartDrawer.setEmptyState(isEmpty);
        } else {
          cartDrawer.classList.toggle('is-empty', isEmpty);
          cartDrawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
          cartDrawer.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
        }

        cartDrawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', cartDrawer.close.bind(cartDrawer));

        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open(triggerElement);
        }

        return true;
      } catch (error) {
        console.error('Featured card drawer refresh failed:', error);
        return false;
      }
    };
  }

  if (!window.__bindFeaturedProductCardForms) {
    window.__bindFeaturedProductCardForms = function (root = document) {
      if (!root || !root.querySelectorAll) return;

      root.querySelectorAll('.featured-products-card form[data-type="add-to-cart-form"]').forEach((form) => {
        if (form.dataset.featuredCardAjaxBound === 'true') return;
        form.dataset.featuredCardAjaxBound = 'true';

        form.addEventListener('submit', async (event) => {
          const productFormElement = form.closest('product-form');
          if (
            productFormElement &&
            productFormElement.form === form &&
            typeof productFormElement.onSubmitHandler === 'function'
          ) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();

          const submitButton = form.querySelector('button[type="submit"]');
          const spinner = form.querySelector('.loading__spinner');
          const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');

          try {
            submitButton?.setAttribute('aria-disabled', 'true');
            submitButton?.classList.add('loading');
            spinner?.classList.remove('hidden');

            const formData = new FormData(form);

            if (cart && typeof cart.getSectionsToRender === 'function') {
              formData.append(
                'sections',
                cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
              cart.setActiveElement?.(document.activeElement);
            }

            const response = await fetch(window.routes.cart_add_url, {
              method: 'POST',
              headers: {
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: formData,
            });
            const parsedState = await response.json();

            if (parsedState.status) throw new Error(parsedState.description || parsedState.message);

            if (cart && typeof cart.renderContents === 'function') {
              cart.renderContents(parsedState);
            } else {
              await window.__openFeaturedProductsCartDrawer?.(submitButton);
            }
          } catch (error) {
            console.error('Featured card add to cart failed:', error);
            await window.__openFeaturedProductsCartDrawer?.(submitButton);
          } finally {
            submitButton?.classList.remove('loading');
            submitButton?.removeAttribute('aria-disabled');
            spinner?.classList.add('hidden');
          }
        }, true);
      });
    };
  }

  function getGap(section) {
    const gap = getComputedStyle(section)
      .getPropertyValue('--featured-products-card-gap')
      .trim();
    return gap || '24px';
  }

  /**
   * Ensure the wrapper is visible when Splide is NOT managing it.
   * We use the is-rendered class: Splide-core's own rule
   * `.splide.is-rendered { visibility: visible }` has specificity (0,2,0)
   * which beats `.splide { visibility: hidden }` at (0,1,0).
   */
  function makeVisible(section) {
    const splideEl = section.querySelector('.featured-products-splide');
    if (splideEl) splideEl.classList.add('is-rendered');
  }

  function mountSplide(section) {
    if (!section || section.dataset.featuredProductsSplideInitialized === 'true') return;
    if (typeof Splide === 'undefined') return;

    const splideEl = section.querySelector('.featured-products-splide');
    if (!splideEl) return;

    // Check if there's only 1 product - if so, don't mount Splide on tablet
    const grid = section.querySelector('.section-featured-products__grid');
    const isSingleProduct = grid && grid.classList.contains('section-featured-products__grid--count-1');
    if (isSingleProduct) {
      makeVisible(section);
      return;
    }

    // Remove is-rendered — Splide JS will add is-initialized which takes over
    splideEl.classList.remove('is-rendered');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const splide = new Splide(splideEl, {
      type: 'slide',
      perPage: 2,
      perMove: 1,
      gap: getGap(section),
      arrows: false,
      pagination: false,
      drag: true,
      speed: prefersReducedMotion ? 0 : 700,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      waitForTransition: false,
      updateOnMove: true,
    });

    splide.mount();
    section.__featuredProductsSplide = splide;
    section.dataset.featuredProductsSplideInitialized = 'true';
  }

  function destroySplide(section) {
    if (section.__featuredProductsSplide) {
      section.__featuredProductsSplide.destroy(true);
      section.__featuredProductsSplide = null;
      section.dataset.featuredProductsSplideInitialized = '';
    }
    // splide.destroy(true) removes is-active but NOT is-initialized.
    // splide-core rule `.splide.is-initialized:not(.is-active) .splide__list { display:block }`
    // has specificity (0,4,0) and would override our display:grid — remove it manually.
    const splideEl = section.querySelector('.featured-products-splide');
    if (splideEl) {
      splideEl.classList.remove('is-initialized');

      // splide.destroy(true) does not reliably remove inline styles it set on the
      // track, list, and slides (e.g. width: calc(50% - 12px), margin-right, transform).
      // Inline styles beat any CSS rule, so manually clear them here.
      const track = splideEl.querySelector('.splide__track');
      if (track) track.removeAttribute('style');

      const list = splideEl.querySelector('.splide__list');
      if (list) list.removeAttribute('style');

      splideEl.querySelectorAll('.splide__slide').forEach((slide) => slide.removeAttribute('style'));
    }
    // Re-assert visibility (is-rendered beats the default visibility:hidden)
    makeVisible(section);
  }

  function syncSection(section) {
    const mq = window.matchMedia(TABLET_QUERY);
    if (mq.matches) {
      mountSplide(section);
    } else {
      destroySplide(section);
    }
  }

  function getAllSections() {
    return document.querySelectorAll('[id^="SectionFeaturedProducts-"]');
  }

  // Debounced resize — prevents rapid mount/destroy cycles mid-drag
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      getAllSections().forEach(syncSection);
    }, 150);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[id^="SectionFeaturedProducts-"]');
    if (section) {
      const splideEl = section.querySelector('.featured-products-splide');
      if (splideEl) splideEl.dataset.featuredProductsSplideInitialized = '';
      syncSection(section);
      window.__bindFeaturedProductCardForms?.(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[id^="SectionFeaturedProducts-"]');
    if (section) destroySplide(section);
  });

  getAllSections().forEach(syncSection);
  window.__bindFeaturedProductCardForms?.(document);
})();
